'use client';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId } from '../utils';
import { TableVirtuoso, TableVirtuosoHandle } from 'react-virtuoso';

type BaseTableAttributes = Pick<
  React.TableHTMLAttributes<HTMLTableElement>,
  'id' | 'className' | 'style' | 'role' | 'onScroll'
>;

const tableVariants = cva('w-full text-left', {
  variants: {
    size: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
      xl: ''
    },
    variant: {
      default: '',
      striped: ''
    },
    rounded: {
      true: ''
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'lg',
    rounded: true
  }
});

type TableVariantProps = VariantProps<typeof tableVariants>;
export interface TableProps<T = unknown> extends TableVariantProps, BaseTableAttributes {
  headers: React.ReactNode[];
  data: T[];
  renderRow?: (row: T, index: number) => React.ReactNode[];
  footer?: React.ReactNode[];
  className?: string;
  sticky?: boolean;
  NoContentFallback?: React.ReactNode;
  rowRef?: React.Ref<React.TdHTMLAttributes<HTMLTableColElement>>;
  selectedRows?: number[];
  rowClassName?: string;
  cellClassName?: string;
  /** Called when user clicks anywhere on a row, including cell padding areas. */
  onRowClick?: (row: T, index: number) => void;
  testId?: string;
}

type TableContext<T> = { onRowClick?: (row: T, index: number) => void; data: T[] };

const arrayRowRenderer = (row: unknown[], index: number): React.ReactNode[] => {
  return row.map((r, rx) => <span key={`table-${index}-${rx}`}>{String(r)}</span>);
};

const TableInner = React.forwardRef<TableVirtuosoHandle, TableProps>(
  (
    {
      headers,
      data,
      renderRow,
      size,
      sticky = true,
      variant,
      rounded = true,
      footer,
      className,
      NoContentFallback,
      selectedRows,
      onScroll,
      cellClassName,
      rowClassName,
      onRowClick,
      testId,
      id,
      ...props
    },
    ref
  ) => {
    const SizeValue = {
      xs: '300px',
      sm: '400px',
      md: '500px',
      lg: '600px',
      xl: '800px'
    }[size ?? 'md'];
    const rowRenderer = renderRow || arrayRowRenderer;

    const context: TableContext<unknown> = {
      onRowClick: onRowClick as ((row: unknown, index: number) => void) | undefined,
      data
    };

    return (
      <div
        className={cn('border border-border-base overflow-hidden', rounded && 'rounded-[12px]')}
        style={{ height: SizeValue }}
      >
        <TableVirtuoso
          ref={ref}
          onScroll={onScroll}
          data={data}
          context={context}
          style={{
            height: SizeValue,
            overflow: 'auto'
          }}
          className='table-scroll-container min-w-full'
          fixedHeaderContent={() => (
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className={cn(
                    'border-b border-border-base p-sm px-md text-foreground-muted font-normal',
                    variant === 'default' ? 'bg-background-input' : 'bg-background-input'
                  )}
                >
                  {header}
                </th>
              ))}
            </tr>
          )}
          fixedFooterContent={() => (
            <tr
              className={cn(variant === 'default' ? 'bg-background-input' : 'bg-background-input')}
            >
              {footer &&
                footer.map((cell, index) => (
                  <td
                    key={index}
                    className='p-sm px-md font-medium text-foreground-muted border-t border-border-base'
                  >
                    {cell}
                  </td>
                ))}
            </tr>
          )}
          components={{
            Table: ({ children, ...tableProps }) => (
              <table
                {...tableProps}
                id={id}
                data-testid={getTestId({ testId, id })}
                className={cn(
                  tableVariants({ size, rounded, variant, className }),
                  'table-fixed overflow-auto',
                  !sticky && 'overflow-hidden'
                )}
                {...props}
              >
                {children}
              </table>
            ),
            TableRow: ({ children, context: ctx, ...props }) => {
              const rawIndex = props['data-index'];
              const index = Number(rawIndex);
              const validIndex = !Number.isNaN(index);
              const isSelected = validIndex && selectedRows?.includes(index);
              const handleRowClick =
                ctx?.onRowClick && validIndex
                  ? () => ctx.onRowClick!(ctx.data[index], index)
                  : undefined;

              return (
                <tr
                  {...props}
                  onClick={handleRowClick}
                  className={cn(
                    'transition ease-out duration-600',
                    handleRowClick && 'cursor-pointer',
                    variant === 'striped'
                      ? 'odd:bg-background-input even:hover:bg-background-input odd:hover:bg-background-input'
                      : 'bg-background-input',
                    isSelected && 'bg-background-muted transition ease-out duration-600', // Highlight selected
                    rowClassName
                  )}
                >
                  {children}
                </tr>
              );
            },
            EmptyPlaceholder: () => (
              <tbody>
                <tr>
                  <td colSpan={headers.length} className='p-0'>
                    <div
                      style={{ height: `calc(${SizeValue} - 50px)` }}
                      className='flex justify-center items-center text-foreground-muted'
                    >
                      {NoContentFallback ?? <p>empty state</p>}
                    </div>
                  </td>
                </tr>
              </tbody>
            )
          }}
          itemContent={(index, row) => {
            const cells = rowRenderer(row as unknown[], index);
            return (
              <React.Fragment key={index}>
                {cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={cn(
                      'border-b text-foreground-muted border-b-border-base p-md',
                      cellClassName
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </React.Fragment>
            );
          }}
        />
      </div>
    );
  }
);

TableInner.displayName = 'Table';

/**
 * Generic data table backed by react-virtuoso.
 * TypeScript infers `T` from the `data` prop — no explicit type argument needed in most cases.
 */
export const Table = TableInner as <T = unknown>(
  props: TableProps<T> & React.RefAttributes<TableVirtuosoHandle>
) => React.ReactElement | null;
